import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TabStrip from '@/components/shared/TabStrip';
import { usePermissions } from '@/hooks/usePermissions';

import BoardTab from './match/BoardTab';
// Sibling thread files — will exist after Wave 3 completes
import SessionsTab from './match/SessionsTab';
import ProviderTemplatesTab from './match/ProviderTemplatesTab';
import SettingsTab from './match/SettingsTab';
import AuditTrailTab from './match/AuditTrailTab';

const BASE_TABS = [
  { id: 'board', label: 'Board', path: '/match/board' },
  { id: 'sessions', label: 'Sessions', path: '/match/sessions' },
  { id: 'provider-templates', label: 'Provider Templates', path: '/match/provider-templates' },
];

const SETTINGS_TAB = { id: 'settings', label: 'Settings', path: '/match/settings' };
const AUDIT_TAB    = { id: 'audit-trail', label: 'Audit Trail', path: '/match/audit-trail' };

function tabIdFromPath(pathname) {
  if (pathname.startsWith('/match/sessions')) return 'sessions';
  if (pathname.startsWith('/match/provider-templates')) return 'provider-templates';
  if (pathname.startsWith('/match/settings')) return 'settings';
  if (pathname.startsWith('/match/audit-trail')) return 'audit-trail';
  return 'board';
}

export default function MatchPortal() {
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  const canViewSettings  = hasPermission('match.settings.view');
  // Audit trail is a reporting projection over rows — same perm as the routes
  // that serve it (match.row.view; match.audit_trail.view is not in the catalog).
  const canViewAudit     = hasPermission('match.row.view');

  const tabs = [
    ...BASE_TABS,
    ...(canViewSettings ? [SETTINGS_TAB] : []),
    ...(canViewAudit   ? [AUDIT_TAB]    : []),
  ];

  const [activeTab, setActiveTab] = useState(() => tabIdFromPath(location.pathname));

  // Keep tab state in sync with URL (back/forward, direct navigation)
  useEffect(() => {
    const id = tabIdFromPath(location.pathname);
    setActiveTab(id);
  }, [location.pathname]);

  const ALL_TABS = [...BASE_TABS, SETTINGS_TAB, AUDIT_TAB];

  const handleTabChange = useCallback(
    (id) => {
      setActiveTab(id);
      const tab = ALL_TABS.find((t) => t.id === id);
      if (tab) navigate(tab.path, { replace: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate],
  );

  // Cross-tab navigation prop consumed by SessionsTab (3B interface contract):
  //   onNavigate(tabId, params?) — e.g. after creating a session, jump to the
  //   Board tab with that session preselected via `?session=<id>`.
  const handleNavigate = useCallback(
    (tabId, params) => {
      const tab = ALL_TABS.find((t) => t.id === tabId);
      if (!tab) return;
      setActiveTab(tabId);
      const qs = params && params.session ? `?session=${encodeURIComponent(params.session)}` : '';
      navigate(`${tab.path}${qs}`);
    },
    [navigate],
  );

  if (!hasPermission('match.access')) {
    return (
      <div className="py-12 text-center text-boss-text-muted">
        You do not have permission to access MATCH.
      </div>
    );
  }

  return (
    <div className="space-y-0" data-testid="match-portal">
      {/* Header — no horizontal padding here: Layout's <main> already applies
          p-6 to every page, so adding px-* again mis-aligns MATCH vs. the
          rest of the app. */}
      <div className="pb-2">
        <h1 className="page-title">MATCH</h1>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-boss-border bg-boss-surface">
        <TabStrip
          tabs={tabs}
          activeId={activeTab}
          onChange={handleTabChange}
          variant="underline"
          size="md"
        />
      </div>

      {/* Tab content */}
      <div className="py-6">
        {activeTab === 'board' && <BoardTab />}
        {activeTab === 'sessions' && <SessionsTab onNavigate={handleNavigate} />}
        {activeTab === 'provider-templates' && <ProviderTemplatesTab />}
        {activeTab === 'settings' && canViewSettings && <SettingsTab />}
        {activeTab === 'audit-trail' && canViewAudit && <AuditTrailTab />}
      </div>
    </div>
  );
}
